import React, { useCallback, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useSearchParams } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Chart,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartTitle,
} from '@progress/kendo-react-charts';
import 'hammerjs';
import Grid from '@material-ui/core/Grid';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PageTitle from 'components/PageTitle';
import SearchCriteria from 'components/SearchCriteria';
import ReportContent from 'components/ReportContent';
import { fetchResource, isResponseError } from 'helpers/fetch';
import { API_CONSTANTS } from 'constants/api';
import { getCounties } from 'state/actions';
import { SEARCH_QUERY_PARAMS } from 'constants/querystring';
import DataGrid from 'components/DataGrid';
import './index.css';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginTop: '15px',
  },

  accordiontitle: {
    backgroundColor: '#37609d',
    color: 'white',
  },

  infomessage: {
    margin: '15px',
  },

  footnote: {
    marginTop: '20px',
    marginRight: '0px',
    marginBottom: '20px',
    marginLeft: '0px',
    fontSize: '0.85em',
    color: '#333333',
    padding: '10px',
  },
}));

export const getCountyWiseData = async (criteria) => {
  const data = await fetchResource(API_CONSTANTS.REPORT_COUNTY_WISE_DATA, {
    method: 'POST',
    data: criteria,
  });
  return data;
};

const TooltipRender = (props) => {
  return (
    <>
      {props.point ? (
        <div style={{ padding: '0.25rem' }}>
          <div>{props.point.category}</div>
        </div>
      ) : null}
    </>
  );
};

const CountyProfilePage = ({
  counties: { loading: countiesLoading, values: counties = [] } = {},
  dispatch,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const _counties = searchParams.get(SEARCH_QUERY_PARAMS.County);
  // eslint-disable-next-line no-unused-vars
  const [searchCriteria, setSearchCriteria] = useState({});
  const [insurerExpanded, setInsurerExpanded] = React.useState(true);
  const [coverageCodeExpanded, setCoverageCodeExpanded] = React.useState(true);
  const [hasInsurerPolicyCountData, setHasInsurerPolicyCountData] = useState(false);
  const [hasInsurerTotalPremiumData, setHasInsurerTotalPremiumData] = useState(false);
  const [hasCoverageCodePolicyCountData, setHasCoverageCodePolicyCountData] = useState(false);
  const [hasCoverageCodeTotalPremiumData, setHasCoverageCodeTotalPremiumData] = useState(false);
  const [selectedCounty, setSelectedCounty] = useState();
  const classes = useStyles();

  const insurerHeader = [
    {
      dataColumn: 'insurer',
      label: 'Insurers',
      numeric: false,
      sortColumn: 'insurer',
    },
    {
      dataColumn: 'policyCount',
      label: 'Policy Count',
      numeric: true,
      sortColumn: 'policyCount',
    },
    {
      dataColumn: 'showTotalPremium',
      label: 'Total Premium',
      numeric: true,
      sortColumn: 'totalPremium',
    },
  ];

  const coverageCodeHeader = [
    {
      dataColumn: 'coverageCode',
      label: 'Coverages',
      numeric: false,
      sortColumn: 'coverageCode',
    },
    {
      dataColumn: 'policyCount',
      label: 'Policy Count',
      numeric: true,
      sortColumn: 'policyCount',
    },
    {
      dataColumn: 'showTotalPremium',
      label: 'Total Premium',
      numeric: true,
      sortColumn: 'totalPremium',
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(getCounties());
  }, [dispatch]);

  useEffect(() => {
    setSelectedCounty(
      counties
        ? counties.length > 0 && Number(_counties) > 0
          ? counties.filter((itm) => {
              return itm.countyId === Number(_counties);
            })[0].countyName
          : null
        : null,
    );
  }, [counties, _counties]);

  const handleSearch = useCallback(async (criteria) => {
    if (Number(criteria.counties[0]) > 0) {
      setLoading(true);
      setSearchCriteria(criteria);
      const response = await getCountyWiseData(criteria);

      if (!isResponseError(response)) {
        setData(response);
        setHasInsurerPolicyCountData(response.insurersPolicyCountGraphData.length > 0);
        setHasInsurerTotalPremiumData(response.insurersTotalPremiumGraphData.length > 0);
        setHasCoverageCodePolicyCountData(response.coverageCodePolicyCountGraphData.length > 0);
        setHasCoverageCodeTotalPremiumData(response.coverageCodeTotalPremiumGraphData.length > 0);
      }
      setInsurerExpanded(true);
      setCoverageCodeExpanded(true);
      setLoading(false);
    }
  }, []);

  const labelContent = (props) => {
    let formatedNumber = Number(props.dataItem.yAxis).toLocaleString(undefined, {
      style: 'percent',
      minimumFractionDigits: 2,
    });
    return `${formatedNumber}`;
  };
  const handleInsurerChange = (panel) => (event, newExpanded) => {
    setInsurerExpanded(newExpanded);
  };
  const handleCoverageCodeChange = (panel) => (event, newExpanded) => {
    setCoverageCodeExpanded(newExpanded);
  };
  return (
    <>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <PageTitle>County Data Sheet {selectedCounty ? `- ${selectedCounty}` : null}</PageTitle>
          <SearchCriteria
            onSearch={handleSearch}
            disableSearch={loading}
            hideEndDate={true}
            hideCoverageCodes={false}
            hideInsurers={false}
            hideCounties={false}
            allowMultipleCounty={false}
            allowMultipleInsurer={true}
            showAllQuarters={true}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div className={'pagebreak'}>
              <Accordion
                expanded={insurerExpanded}
                onChange={handleInsurerChange('insurer')}
                className={'print-accordion'}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="insurer-content"
                  id="insurer-header"
                  className={classes.accordiontitle}
                >
                  <Typography>List of Insurers</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography style={{ width: '100%' }}>
                    <Grid container spacing={2}>
                      {hasInsurerPolicyCountData && hasInsurerTotalPremiumData ? (
                        <>
                          <Grid item sm={6} xs={12}>
                            <ReportContent loading={loading} hasData={hasInsurerPolicyCountData}>
                              <Chart>
                                <ChartTitle text={`Policy Count`} />
                                <ChartLegend position="bottom" visible={false} />
                                <ChartTooltip render={TooltipRender} />
                                <ChartSeries>
                                  <ChartSeriesItem
                                    type="pie"
                                    data={data.insurersPolicyCountGraphData}
                                    field="yAxis"
                                    categoryField="xAxis"
                                    labels={{
                                      visible: true,
                                      content: labelContent,
                                    }}
                                  />
                                </ChartSeries>
                              </Chart>
                            </ReportContent>
                          </Grid>
                          <Grid item sm={6} xs={12}>
                            <ReportContent loading={loading} hasData={hasInsurerTotalPremiumData}>
                              <Chart>
                                <ChartTitle text={`Total Premium`} />
                                <ChartLegend position="bottom" visible={false} />
                                <ChartTooltip render={TooltipRender} />
                                <ChartSeries>
                                  <ChartSeriesItem
                                    type="pie"
                                    data={data.insurersTotalPremiumGraphData}
                                    field="yAxis"
                                    categoryField="xAxis"
                                    labels={{
                                      visible: true,
                                      content: labelContent,
                                    }}
                                  />
                                </ChartSeries>
                              </Chart>
                            </ReportContent>
                          </Grid>
                        </>
                      ) : data.insurerWiseData &&
                        data.insurerWiseData.length >
                          0 ? /*SimonS 12/15/2022 : Check if there is data with Negative values, if so then neither chart is shown nor any message*/ null : (
                        <Grid item xs={12} spacing={5}>
                          <div className={classes.infomessage}>
                            No data to display. Please expand your search criteria and try again.
                          </div>
                        </Grid>
                      )}

                      {hasInsurerTotalPremiumData ? (
                        <Grid item xs={12} spacing={5}>
                          <div className={classes.infomessage}>
                            * The chart does not include insurers with negative premium.
                          </div>
                        </Grid>
                      ) : null}
                      <Grid item xs={12}>
                        {data.insurerWiseData ? (
                          data.insurerWiseData.length > 0 ? (
                            <DataGrid
                              data={data.insurerWiseData}
                              headers={insurerHeader}
                              defaultOrderBy="totalPremium"
                              defaultOrder="desc"
                            ></DataGrid>
                          ) : null
                        ) : null}
                      </Grid>
                    </Grid>
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </div>
          </Grid>
          <Grid item xs={12}>
            <div
              style={{
                pageBreakAfter: 'always',
                breakAfter: 'always',
              }}
            ></div>
            <div className={'pagebreak'}>
              <Accordion
                expanded={coverageCodeExpanded}
                onChange={handleCoverageCodeChange('coveragecode')}
                className={'print-accordion'}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="coveragecode-content"
                  id="coveragecode-header"
                  className={classes.accordiontitle}
                >
                  <Typography>List of Coverages</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography style={{ width: '100%' }}>
                    <Grid container spacing={2}>
                      {hasCoverageCodePolicyCountData && hasCoverageCodeTotalPremiumData ? (
                        <>
                          <Grid item sm={6} xs={12}>
                            <ReportContent
                              loading={loading}
                              hasData={hasCoverageCodePolicyCountData}
                            >
                              <Chart>
                                <ChartTitle text={`Policy Count`} />
                                <ChartLegend position="bottom" visible={false} />
                                <ChartTooltip render={TooltipRender} />
                                <ChartSeries>
                                  <ChartSeriesItem
                                    type="pie"
                                    data={data.coverageCodePolicyCountGraphData}
                                    field="yAxis"
                                    categoryField="xAxis"
                                    labels={{
                                      visible: true,
                                      content: labelContent,
                                    }}
                                  />
                                </ChartSeries>
                              </Chart>
                            </ReportContent>
                          </Grid>
                          <Grid item sm={6} xs={12}>
                            <ReportContent
                              loading={loading}
                              hasData={hasCoverageCodeTotalPremiumData}
                            >
                              <Chart>
                                <ChartTitle text={`Total Premium`} />
                                <ChartLegend position="bottom" visible={false} />
                                <ChartTooltip render={TooltipRender} />
                                <ChartSeries>
                                  <ChartSeriesItem
                                    type="pie"
                                    data={data.coverageCodeTotalPremiumGraphData}
                                    field="yAxis"
                                    categoryField="xAxis"
                                    labels={{
                                      visible: true,
                                      content: labelContent,
                                    }}
                                  />
                                </ChartSeries>
                              </Chart>
                            </ReportContent>
                          </Grid>
                        </>
                      ) : data.coverageCodeWiseData &&
                        data.coverageCodeWiseData.length >
                          0 ? /*SimonS 12/15/2022 : Check if there is data with Negative values, if so then neither chart is shown nor any message*/ null : (
                        <Grid item xs={12}>
                          <div className={classes.infomessage}>
                            No data to display. Please expand your search criteria and try again.
                          </div>
                        </Grid>
                      )}

                      {hasCoverageCodeTotalPremiumData ? (
                        <Grid item xs={12}>
                          <div className={classes.infomessage}>
                            * The chart does not include coverage codes with negative premium.
                          </div>
                        </Grid>
                      ) : null}
                      <Grid item xs={12}>
                        {data.coverageCodeWiseData ? (
                          data.coverageCodeWiseData.length > 0 ? (
                            <DataGrid
                              data={data.coverageCodeWiseData}
                              headers={coverageCodeHeader}
                              defaultOrderBy="totalPremium"
                              defaultOrder="desc"
                            ></DataGrid>
                          ) : null
                        ) : null}
                      </Grid>
                    </Grid>
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <div className={classes.footnote}>
            <strong>Note:</strong> The information included in this report is inclusive of
            single-state Mississippi policy filings only as reported to the Mississippi Surplus
            Lines Association. For the Insurers By Premium By County Report, "Policy Count" refers
            to the number of policies in which the insurer is associated. For a count of policies
            written in the market, please refer to the Fast Facts Report.
          </div>
        </Grid>
      </Grid>
    </>
  );
};

const mapStateToProps = ({ counties = { loading: true, values: [] } }) => ({
  counties,
});

export default connect(mapStateToProps)(CountyProfilePage);
